import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatRange, formatDateRow, formatMoney } from "../lib/format";
import { useConfirm, useAlert } from "../components/Confirm";
import { CommentThread } from "../components/CommentThread";
import { TypeBadge } from "../components/EstimateTable";

interface DayRow {
  day_date: string;
  day_index: number;
  is_return_day: boolean;
  incremental_day_hours: number;
  incremental_night_hours: number;
  sleep_in_shifts: number;
}

export function ApprovalDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const est = useQuery({ queryKey: ["est-admin", id], queryFn: () => api.getEstimate(id) });
  const rates = useQuery({ queryKey: ["rates"], queryFn: api.rateCard });
  const related = useQuery({ queryKey: ["related-pws", id], queryFn: () => api.relatedByPws(id) });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  const [reason, setReason] = useState("");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjEdit, setAdjEdit] = useState<Record<string, { amount: string; reason: string }>>({});
  const [days, setDays] = useState<DayRow[]>([]);
  const [savedDays, setSavedDays] = useState<DayRow[]>([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const confirm = useConfirm();
  const alertModal = useAlert();

  useEffect(() => {
    if (!est.data?.days) return;
    const next: DayRow[] = est.data.days.map((d: any) => ({
      day_date: d.day_date?.slice(0, 10),
      day_index: d.day_index,
      is_return_day: !!d.is_return_day,
      incremental_day_hours: Number(d.incremental_day_hours),
      incremental_night_hours: Number(d.incremental_night_hours),
      sleep_in_shifts: Number(d.sleep_in_shifts),
    }));
    setDays(next);
    setSavedDays(next);
  }, [est.data]);

  // Prefer the estimate-scoped rates (set on every CALCULATE; frozen on submit).
  // Fall back to the live rate card for brand-new drafts and during initial load.
  const r = est.data?.rates ?? rates.data?.rates ?? {};
  const dayRate     = (r.day_hour?.pence ?? 0) / 100;
  const nightRate   = (r.night_hour?.pence ?? 0) / 100;
  const sleepRate   = (r.sleep_in?.pence ?? 0) / 100;
  const expensesRate = (r.staff_expense_per_day?.pence ?? 0) / 100;
  const staff = est.data?.staff_count ?? 1;

  const liveTotals = useMemo(() => {
    let total = 0;
    const rowSubs = days.map((d) => {
      // Per Nick (2026-05-27): staff count only multiplies Staff Expenses.
      const day   = d.incremental_day_hours   * dayRate;
      const night = d.is_return_day ? 0 : d.incremental_night_hours * nightRate;
      const sleep = d.is_return_day ? 0 : d.sleep_in_shifts * sleepRate;
      const sub = day + night + sleep;
      total += sub;
      return sub;
    });
    const expensesTotal = days.length * expensesRate * staff;
    const adjTotal = (est.data?.adjustments ?? []).reduce((s: number, a: any) => s + Number(a.amount_pence) / 100, 0);
    return { rowSubs, schedTotal: total, expensesTotal, adjTotal, grandTotal: total + expensesTotal + adjTotal };
  }, [days, dayRate, nightRate, sleepRate, expensesRate, staff, est.data?.adjustments]);

  const dirtySchedule = useMemo(
    () => JSON.stringify(days) !== JSON.stringify(savedDays),
    [days, savedDays],
  );

  if (est.isLoading) return <div className="text-slate-500">Loading...</div>;
  if (!est.data) return <div className="text-red-600">Not found.</div>;
  const e = est.data;

  async function saveSchedule() {
    setSavingSchedule(true);
    try {
      await api.replaceDays(id, days);
      await qc.invalidateQueries({ queryKey: ["est-admin", id] });
    } finally {
      setSavingSchedule(false);
    }
  }
  async function approve() {
    if (dirtySchedule) await saveSchedule();
    await api.approvalAction(id, "approve", { reason: reason || null });
    nav("/");
  }
  async function reject() {
    if (!reason.trim()) {
      await alertModal({ title: "Reason required", body: "Please add a reason in the box below before rejecting.", confirmLabel: "OK" });
      return;
    }
    await api.approvalAction(id, "reject", { reason });
    nav("/");
  }
  async function revertToDraft() {
    const ok = await confirm({
      title: "Send back to draft?",
      body: "The claimant will need to resubmit before this can be approved.",
      confirmLabel: "Send back",
      cancelLabel: "Keep in queue",
      warning: true,
    });
    if (!ok) return;
    await api.approvalAction(id, "revert", { to_status: "draft", reason: reason || "Sent back" });
    nav("/");
  }
  async function addAdjustment() {
    const n = Math.round(Number(adjAmount) * 100);
    if (!Number.isFinite(n) || n === 0) {
      await alertModal({ title: "Amount required", body: "Enter a non-zero amount in £.", confirmLabel: "OK" });
      return;
    }
    if (!adjReason.trim()) {
      await alertModal({ title: "Reason required", body: "Add a short reason for the adjustment.", confirmLabel: "OK" });
      return;
    }
    await api.adjustmentCreate(id, n, adjReason);
    setAdjAmount(""); setAdjReason("");
    await qc.invalidateQueries({ queryKey: ["est-admin", id] });
  }
  async function saveAdjustment(adjId: string) {
    const draft = adjEdit[adjId];
    if (!draft) return;
    const n = Math.round(Number(draft.amount) * 100);
    if (!Number.isFinite(n) || n === 0) {
      await alertModal({ title: "Amount required", body: "Enter a non-zero amount in £.", confirmLabel: "OK" });
      return;
    }
    if (!draft.reason.trim()) {
      await alertModal({ title: "Reason required", body: "Add a short reason for the adjustment.", confirmLabel: "OK" });
      return;
    }
    await api.adjustmentUpdate(id, adjId, n, draft.reason);
    setAdjEdit((s) => ({ ...s, [adjId]: undefined as any }));
    await qc.invalidateQueries({ queryKey: ["est-admin", id] });
  }
  async function deleteAdjustment(adjId: string) {
    const ok = await confirm({
      title: "Delete this adjustment?",
      body: "The estimate total will recalculate immediately.",
      confirmLabel: "Delete adjustment",
      cancelLabel: "Keep",
      danger: true,
    });
    if (!ok) return;
    await api.adjustmentDelete(id, adjId);
    await qc.invalidateQueries({ queryKey: ["est-admin", id] });
  }

  function updateDay(i: number, patch: Partial<DayRow>) {
    setDays((prev) => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  }

  const editable = e.status === "submitted";

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-liaise-navy">{e.request_number}</h1>
            <TypeBadge type={e.type ?? "holiday"} />
          </div>
          <div className="text-sm text-slate-500 mt-1 capitalize">{e.status}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {related.data && e.pws_id && Number(related.data.totals?.approved_this_fy ?? 0) > 0 && (
            <div className="text-xs px-3 py-1.5 rounded-full bg-liaise-blue/10 text-liaise-navy">
              <b>{e.pws_name ?? e.pws_initials} YTD:</b> {formatMoney(related.data.totals.approved_value_this_fy)} approved ({related.data.totals.approved_this_fy} holiday{related.data.totals.approved_this_fy === 1 ? "" : "s"})
            </div>
          )}
          <Link to="/" className="text-sm text-slate-500 hover:underline">← Back to queue</Link>
        </div>
      </div>

      {/* Linked context: who/what/where */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="grid sm:grid-cols-4 gap-x-6 gap-y-4 text-sm">
          <ContextCell label="Claimant"
            value={e.claimant_entra_oid
              ? <Link to={`/claimant/${e.claimant_entra_oid}`} className="text-liaise-blue hover:underline">{e.claimant_display_name}</Link>
              : e.claimant_display_name}
            sub={`${e.claimant_email ?? ""}${e.job_title ? ` · ${e.job_title}` : ""}`} />
          <ContextCell label="Person we support"
            value={e.pws_id
              ? <Link to={`/pws/${e.pws_id}`} className="text-liaise-blue hover:underline">{e.pws_name ?? e.pws_initials}</Link>
              : (e.pws_initials ?? "-")}
            sub={`${e.pws_initials ?? ""}${e.pws_acuity ? ` · ${e.pws_acuity} acuity` : ""}`} />
          <ContextCell label="Home"
            value={e.home_id
              ? <Link to={`/home/${e.home_id}`} className="text-liaise-blue hover:underline">{e.home_name}</Link>
              : (e.home_name ?? "-")} />
          <ContextCell label="Funder"
            value={e.funder_id
              ? <Link to={`/funder/${e.funder_id}`} className="text-liaise-blue hover:underline">{e.funder_name}</Link>
              : (e.funder_name ?? "-")}
            sub={e.snapshot_funder_case_by_case ? "⚠ Case-by-Case" : undefined}
            subColor="text-orange-700" />
        </div>

        {/* Annual contribution / Partnership RAG removed from H/H/T views per James (2026-05-20).
            These figures aren't relevant to incremental holiday pricing and have caused
            issues when forwarded to funders. Still visible on PwsProfile if needed. */}
        <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <div><span className="text-slate-500">Dates:</span> <span className="font-semibold">{formatRange(e.start_date, e.end_date)}</span></div>
        </div>
      </div>

      {/* Case-by-case breakdown — read-only, mirrors the claimant's preview */}
      {e.type === "case" && <CaseBreakdownPanel estimate={e} />}

      {/* Schedule — editable on submitted estimates. Hidden for case-by-case. */}
      {e.type !== "case" && (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex items-baseline justify-between">
          <div className="text-sm font-semibold text-liaise-navy">Day-by-day schedule</div>
          <div className="text-xs text-slate-500">
            Day {formatMoney(dayRate)}/hr · Night {formatMoney(nightRate)}/hr · Sleep-in {formatMoney(sleepRate)}/shift · Staff exp {formatMoney(expensesRate)}/day
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-right px-4 py-2">Day hrs</th>
              <th className="text-right px-4 py-2">Night hrs</th>
              <th className="text-right px-4 py-2">Sleep-ins</th>
              <th className="text-right px-4 py-2">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-2 whitespace-nowrap">{formatDateRow(d.day_date)}{d.is_return_day ? " (return)" : ""}</td>
                <td className="px-4 py-2 text-right">
                  <NumberCell value={d.incremental_day_hours} disabled={!editable}
                    onChange={(v) => updateDay(i, { incremental_day_hours: v })} />
                </td>
                <td className="px-4 py-2 text-right">
                  <NumberCell value={d.incremental_night_hours} disabled={!editable || d.is_return_day}
                    onChange={(v) => updateDay(i, { incremental_night_hours: v })} />
                </td>
                <td className="px-4 py-2 text-right">
                  <NumberCell value={d.sleep_in_shifts} step={1} disabled={!editable || d.is_return_day}
                    onChange={(v) => updateDay(i, { sleep_in_shifts: v })} />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{formatMoney(liveTotals.rowSubs[i] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td colSpan={4} className="px-4 py-2 text-right text-slate-600">Staffing subtotal</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatMoney(liveTotals.schedTotal)}</td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan={4} className="px-4 py-2 text-right text-slate-600">Staff expenses ({days.length} days × {staff} staff × {formatMoney(expensesRate)}/day)</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatMoney(liveTotals.expensesTotal)}</td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan={4} className="px-4 py-2 text-right text-slate-600">Adjustments</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatMoney(liveTotals.adjTotal)}</td>
            </tr>
            <tr className="border-t-2 border-slate-200 bg-slate-50">
              <td colSpan={4} className="px-4 py-2 text-right font-semibold">Total (live)</td>
              <td className="px-4 py-2 text-right tabular-nums font-semibold text-liaise-navy">{formatMoney(liveTotals.grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
        {editable && (
          <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-3 bg-slate-50">
            <button onClick={saveSchedule} disabled={!dirtySchedule || savingSchedule}
              className="px-3 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-40">
              {savingSchedule ? "Saving..." : "Save schedule changes"}
            </button>
            <button onClick={() => setDays(savedDays)} disabled={!dirtySchedule}
              className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm disabled:opacity-40">
              Discard changes
            </button>
            {dirtySchedule && <span className="text-xs text-amber-700">Unsaved edits — approve will save automatically.</span>}
          </div>
        )}
      </div>
      )}

      {/* Adjustments — editable + deletable */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-3">
        <div className="text-sm font-semibold text-liaise-navy">Adjustments</div>
        {(e.adjustments ?? []).length === 0 && <div className="text-xs text-slate-500">No adjustments yet.</div>}
        {(e.adjustments ?? []).map((a: any) => {
          const draft = adjEdit[a.id];
          if (draft) {
            return (
              <div key={a.id} className="grid grid-cols-[120px_1fr_auto_auto] gap-2 border-b border-slate-100 pb-2">
                <input type="number" step="0.01" value={draft.amount}
                  onChange={(ev) => setAdjEdit((s) => ({ ...s, [a.id]: { ...draft, amount: ev.target.value } }))}
                  className="px-3 py-2 border border-slate-200 rounded text-sm" />
                <input value={draft.reason}
                  onChange={(ev) => setAdjEdit((s) => ({ ...s, [a.id]: { ...draft, reason: ev.target.value } }))}
                  className="px-3 py-2 border border-slate-200 rounded text-sm" />
                <button onClick={() => saveAdjustment(a.id)} className="px-3 py-2 bg-liaise-navy text-white rounded text-sm">Save</button>
                <button onClick={() => setAdjEdit((s) => ({ ...s, [a.id]: undefined as any }))} className="px-3 py-2 hover:underline text-sm">Cancel</button>
              </div>
            );
          }
          return (
            <div key={a.id} className="text-sm flex items-center justify-between border-b border-slate-100 pb-2">
              <span className="text-slate-600">{a.reason}</span>
              <div className="flex items-center gap-3">
                <span className="tabular-nums font-medium">{formatMoney(Number(a.amount_pence) / 100)}</span>
                {editable && (
                  <>
                    <button onClick={() => setAdjEdit((s) => ({ ...s, [a.id]: { amount: (a.amount_pence / 100).toFixed(2), reason: a.reason } }))}
                      className="text-xs text-liaise-blue hover:underline">Edit</button>
                    <button onClick={() => deleteAdjustment(a.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {editable && (
          <div className="grid grid-cols-[120px_1fr_auto] gap-2 pt-2">
            <input type="number" step="0.01" placeholder="£" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} className="px-3 py-2 border border-slate-200 rounded text-sm" />
            <input placeholder="Reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className="px-3 py-2 border border-slate-200 rounded text-sm" />
            <button onClick={addAdjustment} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded text-sm">Add adjustment</button>
          </div>
        )}
      </div>

      {/* Other estimates for the same PWS — last context before decision */}
      {related.data && e.pws_id && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-100 flex items-baseline justify-between gap-4 flex-wrap">
            <div className="text-sm font-semibold text-liaise-navy">
              Other estimates for {e.pws_name ?? e.pws_initials}
            </div>
            <div className="text-xs text-slate-500">
              {Number(related.data.totals?.approved_count ?? 0)} previously approved · {formatMoney(related.data.totals?.approved_value ?? 0)} total
              {Number(related.data.totals?.approved_this_fy ?? 0) > 0 && (
                <> · This FY: {related.data.totals.approved_this_fy} ({formatMoney(related.data.totals.approved_value_this_fy)})</>
              )}
            </div>
          </div>
          {(related.data.related ?? []).length === 0 ? (
            <div className="p-6 text-slate-500 text-sm">No other estimates for this PWS.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Ref</th>
                  <th className="text-left px-4 py-2">Claimant</th>
                  <th className="text-left px-4 py-2">Home / Funder</th>
                  <th className="text-left px-4 py-2">Dates</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {related.data.related.map((row: any) => (
                  <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/approvals/${row.id}`} className="text-liaise-blue hover:underline">{row.request_number}</Link>
                    </td>
                    <td className="px-4 py-2">
                      {row.claimant_entra_oid ? (
                        <Link to={`/claimant/${row.claimant_entra_oid}`} className="text-liaise-blue hover:underline">{row.claimant_display_name}</Link>
                      ) : row.claimant_display_name}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{row.home_name ?? "-"} · <span className="text-slate-500">{row.funder_name ?? "-"}</span></td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatRange(row.start_date, row.end_date)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatMoney(row.total_amount)}</td>
                    <td className="px-4 py-2 text-xs">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {e.status === "submitted" ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-3">
          <label className="block text-sm">
            <span className="text-xs font-medium text-slate-500">Reason (required for reject)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
          </label>
          <div className="flex items-center gap-3">
            <button onClick={approve} className="px-4 py-2 rounded-lg bg-liaise-green text-white text-sm font-medium">
              {dirtySchedule ? "Save + Approve" : "Approve"}
            </button>
            <button onClick={reject}  className="px-4 py-2 rounded-lg bg-liaise-red text-white text-sm font-medium">Reject</button>
            <button onClick={revertToDraft} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm">Send back to draft</button>
          </div>
        </div>
      ) : (
        <NonSubmittedNotice
          status={e.status}
          claimantEmail={e.claimant_email}
          claimantName={e.claimant_display_name}
          statusReason={e.status_reason}
          estimateId={id}
          /* funderSentAt / onSendToFunder removed 2026-05-20 — send-to-funder is
             permanently disabled per James, backend returns 410. Approvers send
             the approved quote from Outlook directly. */
          onSubmitOnBehalf={e.status === "draft" ? async () => {
            const ok = await confirm({
              title: `Submit this draft on behalf of ${e.claimant_display_name ?? "the claimant"}?`,
              body: `${e.claimant_display_name ?? "The claimant"} will be notified and the estimate will enter the approval queue. The audit log will show you (${me.data?.email ?? "you"}) as the submitter.`,
              confirmLabel: "Submit on their behalf",
              cancelLabel: "Not yet",
              warning: true,
            });
            if (!ok) return;
            await api.estimateAction(id, "submit");
            await qc.invalidateQueries({ queryKey: ["est-admin", id] });
            await qc.invalidateQueries({ queryKey: ["approvals"] });
          } : undefined}
        />
      )}

      <CommentThread estimateId={id} currentOid={me.data?.oid ?? ""} isAdmin={!!me.data?.roles?.admin} />

      <ActivityFeed approvals={e.approvals ?? []} adjustments={e.adjustments ?? []} />
    </div>
  );
}

interface FeedEntry { kind: "approval" | "adjustment"; when: string; actor?: string; line: string }

function ActivityFeed({ approvals, adjustments }: { approvals: any[]; adjustments: any[] }) {
  const entries: FeedEntry[] = [
    ...approvals.map((a) => ({
      kind: "approval" as const,
      when: a.acted_at,
      actor: a.actor_email,
      line: `${capitalize(a.action)}${a.reason ? ` — "${a.reason}"` : ""}`,
    })),
    ...adjustments.map((adj) => ({
      kind: "adjustment" as const,
      when: adj.created_at,
      actor: adj.created_by_email,
      line: `Adjustment ${adj.amount_pence >= 0 ? "+" : "-"}£${Math.abs(adj.amount_pence / 100).toFixed(2)} — ${adj.reason}`,
    })),
  ].sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());

  if (!entries.length) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-2">
      <div className="text-sm font-semibold text-liaise-navy">Activity</div>
      {entries.map((e, i) => (
        <div key={i} className="text-xs flex justify-between gap-3 border-b border-slate-100 pb-1.5">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${e.kind === "adjustment" ? "bg-liaise-orange" : "bg-liaise-blue"}`} />
            <span className="text-slate-700 truncate">{e.line}</span>
            {e.actor && <span className="text-slate-400 hidden sm:inline">· {e.actor}</span>}
          </div>
          <span className="text-slate-400 whitespace-nowrap">{new Date(e.when).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      ))}
    </div>
  );
}

function capitalize(s: string) { return s ? s[0].toUpperCase() + s.slice(1) : s; }

function NonSubmittedNotice({ status, claimantEmail, claimantName, statusReason, estimateId, onSubmitOnBehalf }: {
  status: string;
  claimantEmail?: string | null;
  claimantName?: string | null;
  statusReason?: string | null;
  estimateId?: string;
  onSubmitOnBehalf?: () => void;
}) {
  const config: Record<string, { ring: string; title: string; body: string }> = {
    draft: {
      ring: "border-slate-300 bg-slate-50",
      title: "Draft, not yet submitted",
      body: `This estimate is still being prepared${claimantEmail ? ` by ${claimantName ?? claimantEmail}` : ""}. You can review the schedule and context, but nothing can be approved or rejected until it's submitted.`,
    },
    approved: {
      ring: "border-liaise-green/40 bg-liaise-green/5",
      title: "Approved",
      body: "Approved and ready. Forward the approved estimate to the funder from your email client when you're happy to dispatch.",
    },
    rejected: {
      ring: "border-liaise-red/40 bg-liaise-red/5",
      title: "Rejected",
      body: statusReason ? `Rejection reason: "${statusReason}".` : "The claimant can reopen and resubmit.",
    },
    recalled: {
      ring: "border-slate-300 bg-slate-50",
      title: "Recalled by claimant",
      body: statusReason ? `Recall reason: "${statusReason}". The claimant can reopen and resubmit.` : "The claimant pulled this back before approval. They can reopen and resubmit when ready.",
    },
  };
  const c = config[status] ?? config.draft;
  return (
    <div className={`rounded-2xl border-2 p-6 ${c.ring}`}>
      <h3 className="text-sm font-semibold text-liaise-navy">{c.title}</h3>
      <p className="text-sm text-slate-700 mt-2">{c.body}</p>
      {onSubmitOnBehalf && status === "draft" && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-2">If the claimant thought they'd already submitted, you can submit it on their behalf. They'll be notified and the audit log will record you as the submitter.</p>
          <button onClick={onSubmitOnBehalf}
            className="px-3 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium">
            Submit on claimant's behalf
          </button>
        </div>
      )}
      {/* "Send to funder" button removed 2026-05-20 per James — too easy to press
          accidentally, and approvers should send the quote from Outlook directly
          using the approved PDF / preview card. Backend endpoint also returns 410. */}
    </div>
  );
}

/**
 * Read-only case-by-case breakdown shown to approvers. Hours and cost stack
 * come from pricing.estimate_case.* set by the claimant's Save & recalculate.
 * If approver wants to change inputs, recall the estimate.
 */
function CaseBreakdownPanel({ estimate }: { estimate: any }) {
  const cd = estimate.case_data;
  if (!cd) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-sm text-slate-500">
        No case-by-case inputs recorded yet.
      </div>
    );
  }
  const pence = (k: string) => Number(cd[k] ?? 0) / 100;
  const lines: Array<{ label: string; value: number }> = [
    { label: "Management",       value: pence("mgmt_per_week_pence") },
    { label: "Overheads",        value: pence("overheads_per_week_pence") },
    { label: "Central support",  value: pence("central_support_per_week_pence") },
    { label: "Property",         value: pence("property_per_week_pence") },
    { label: "Regional support", value: pence("regional_per_week_pence") },
    { label: "Shared day support",   value: pence("shared_day_per_week_pence") },
    { label: "Shared night support", value: pence("shared_night_per_week_pence") },
    { label: "1:1 staffing",     value: pence("ones_per_week_pence") },
    { label: "2:1 staffing",     value: pence("twos_per_week_pence") },
    { label: "Dedicated night",  value: pence("dedicated_per_week_pence") },
    { label: "Sleep-ins",        value: pence("sleep_in_per_week_pence") },
  ].filter((l) => l.value !== 0);
  if (pence("ha_income_per_week_pence") !== 0) {
    lines.push({ label: "HA income (offset)", value: -Math.abs(pence("ha_income_per_week_pence")) });
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-3 border-b border-slate-100 flex items-baseline justify-between">
        <div className="text-sm font-semibold text-liaise-navy">Case-by-case breakdown</div>
        <div className="text-xs text-slate-500">
          Mode: {cd.shared_hours_mode} · Margin target {(Number(cd.contribution_target_pct) * 100).toFixed(1)}% · FY {cd.fiscal_year}
          {cd.acuity   && <> · Acuity: <b>{cd.acuity}</b></>}
          {cd.division && <> · Division: <b>{cd.division}</b></>}
        </div>
      </div>
      {cd.notes && (
        <div className="px-6 py-3 bg-amber-50/40 border-b border-amber-100 text-xs text-slate-700 whitespace-pre-wrap">
          <b className="text-amber-800">Claimant notes:</b> {cd.notes}
        </div>
      )}
      <div className="p-6 grid lg:grid-cols-2 gap-6 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Hours per week (as submitted)</div>
          <KV label="Core day"             v={cd.core_day_hours} />
          <KV label="Core night"           v={cd.core_night_hours} />
          <KV label="Core sleep-ins"       v={cd.core_sleep_in_shifts} />
          <KV label="1:1 day"              v={cd.ones_day_hours} />
          <KV label="1:1 night"            v={cd.ones_night_hours} />
          <KV label="2:1 day"              v={cd.twos_day_hours} />
          <KV label="2:1 night"            v={cd.twos_night_hours} />
          <KV label="Dedicated night"      v={cd.dedicated_night_hours} />
          <KV label="Extra sleep-ins"      v={cd.additional_sleep_in_shifts} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Cost stack (per week)</div>
          {lines.map((l) => (
            <div key={l.label} className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-700">{l.label}</span>
              <span className="tabular-nums text-slate-800">{formatMoney(l.value)}</span>
            </div>
          ))}
          <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 font-semibold text-liaise-navy">
            <span>Total cost / wk</span>
            <span className="tabular-nums">{formatMoney(pence("total_cost_per_week_pence"))}</span>
          </div>
          <div className="flex justify-between mt-1 font-semibold text-liaise-navy">
            <span>Target fee / wk</span>
            <span className="tabular-nums">{formatMoney(pence("target_fee_per_week_pence"))}</span>
          </div>
          <div className="flex justify-between mt-1 text-base font-bold text-liaise-navy">
            <span>Annual fee</span>
            <span className="tabular-nums">{formatMoney(pence("annual_fee_pence"))}</span>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Implied margin: {(Number(cd.implied_margin_pct) * 100).toFixed(1)}%
            {cd.calc_at && <> · last calc {new Date(cd.calc_at).toLocaleDateString("en-GB")}</>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ label, v }: { label: string; v: number | string }) {
  return (
    <div className="flex justify-between py-1 border-b border-slate-100 text-slate-700">
      <span>{label}</span>
      <span className="tabular-nums">{Number(v).toFixed(1)}</span>
    </div>
  );
}

function ContextCell({ label, value, sub, subColor }: { label: string; value: React.ReactNode; sub?: string; subColor?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-liaise-navy mt-1">{value}</div>
      {sub && <div className={`text-xs ${subColor ?? "text-slate-500"} mt-1`}>{sub}</div>}
    </div>
  );
}

function NumberCell({ value, onChange, disabled, step = 0.5 }: { value: number; onChange: (n: number) => void; disabled?: boolean; step?: number }) {
  return (
    <input type="number" step={step} min={0} value={value} disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value || 0))}
      className="w-20 text-right text-sm border border-slate-200 rounded px-2 py-1 disabled:bg-slate-100 disabled:text-slate-400" />
  );
}
